import { useEffect, useState } from "react";
import {
  Calendar,
  Clock,
  Package,
  User,
  Clipboard,
  FileText,
  Scale,
  Tag,
  Truck,
  ChevronDown,
  ChevronUp,
  ArrowLeft,
  Printer,
  Download,
  Share2,
  Star,
  StarOff,
  AlertCircle,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useParams } from "react-router-dom";
import { getMaterialListByCustomId } from "../../../../Services/materialListWithProjectService";
import MainLayout from "../../../components/layout/MainLayout";
import DeleteConfirmationModal from "../../projects/components/DeleteConfirmationModal";

export default function MaterialListView() {
  const { materialId } = useParams();
  const [materialListData, setMaterialListData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [expandedSections, setExpandedSections] = useState({
    materials: true,
    signatures: false,
    details: false,
  });
  const [isFavorite, setIsFavorite] = useState(false);

  useEffect(() => {
    if (materialId) {
      getMaterialList();
    } else {
      setError("Material ID is missing");
      setLoading(false);
    }
  }, [materialId]);

  const getMaterialList = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await getMaterialListByCustomId(materialId);

      if (!response || !response.data) {
        throw new Error("Failed to fetch material list data");
      }

      const materialData = response.data;
      setMaterialListData(materialData);
    } catch (err) {
      console.error("Error fetching material list:", err);
      setError(
        err instanceof Error ? err.message : "An unknown error occurred"
      );
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";

    try {
      const options = { year: "numeric", month: "long", day: "numeric" };
      return new Date(dateString).toLocaleDateString("en-US", options);
    } catch (error) {
      console.error("Error formatting date:", error);
      return "Invalid date";
    }
  };

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const calculateTotalWeight = () => {
    if (
      !materialListData.materialList ||
      !Array.isArray(materialListData.materialList)
    ) {
      return "0.00";
    }

    try {
      return materialListData.materialList
        .reduce((total, item) => {
          const quantity = Number(item.quantity) || 0;
          const weight = Number(item.kg) || 0;
          return total + weight * quantity;
        }, 0)
        .toFixed(2);
    } catch (error) {
      console.error("Error calculating total weight:", error);
      return "0.00";
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "latest":
        return "bg-blue-100 text-blue-800";
      case "under":
        return "bg-yellow-100 text-yellow-800";
      case "closed":
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "latest":
        return "Latest";
      case "under":
        return "Under Progress";
      case "closed":
        return "Closed";
      default:
        return status || "Unknown";
    }
  };

  // Error state display
  if (error) {
    return (
      <MainLayout title="Error - Material List Details">
        <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="bg-white rounded-xl shadow-sm overflow-hidden p-6">
              <div className="flex flex-col items-center justify-center text-center gap-4">
                <AlertCircle className="text-red-500" size={48} />
                <h1 className="text-2xl font-bold text-gray-900">
                  Error Loading Material List
                </h1>
                <p className="text-gray-600 max-w-md">{error}</p>
                <button
                  onClick={() => window.history.back()}
                  className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <ArrowLeft className="mr-2" size={16} />
                  Back to Material Lists
                </button>
                <button
                  onClick={() => getMaterialList()}
                  className="mt-2 inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
        </div>
      </MainLayout>
    );
  }

  // Loading state display
  if (loading) {
    return (
      <MainLayout title="Loading - Material List Details">
        <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="bg-white rounded-xl shadow-sm overflow-hidden p-6">
              <div className="flex flex-col items-center justify-center text-center gap-4">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                <h1 className="text-xl font-medium text-gray-700">
                  Loading Material List...
                </h1>
              </div>
            </div>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout
      title={materialListData.materialListName || "Material List Details"}
    >
      <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto mb-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <button
              onClick={() => window.history.back()}
              className="inline-flex items-center text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="mr-2" size={20} />
              <span>Back to Material Lists</span>
            </button>

            {/* <div className="flex items-center gap-3">
              <button
                onClick={() => setIsFavorite(!isFavorite)}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                title={
                  isFavorite ? "Remove from favorites" : "Add to favorites"
                }
              >
                {isFavorite ? (
                  <Star
                    className="text-yellow-500"
                    size={20}
                    fill="currentColor"
                  />
                ) : (
                  <StarOff className="text-gray-400" size={20} />
                )}
              </button>
              <button
                className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                title="Print material list"
              >
                <Printer className="text-gray-600" size={20} />
              </button>
              <button
                className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                title="Download as PDF"
              >
                <Download className="text-gray-600" size={20} />
              </button>
              <button
                className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                title="Share material list"
              >
                <Share2 className="text-gray-600" size={20} />
              </button>
            </div> */}
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-7xl mx-auto mb-8 bg-white rounded-xl shadow-sm overflow-hidden"
        >
          <div className="p-6 sm:p-8 border-b border-gray-200">
            <div className="flex flex-col md:flex-row justify-between gap-6">
              <div className="flex-1">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                  {materialListData.materialListName ||
                    "Untitled Material List"}
                </h1>
                <div className="flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-gray-500">
                  <div className="flex items-center">
                    <Package className="mr-2" size={18} />
                    <span>
                      Project: {materialListData.projectName || "N/A"}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <User className="mr-2" size={18} />
                    <span>
                      Customer: {materialListData.customerName || "N/A"}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <Calendar className="mr-2" size={18} />
                    <span>
                      Date: {formatDate(materialListData?.projectDate)}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex flex-col items-start md:items-end gap-2">
                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(
                    materialListData.status
                  )}`}
                >
                  {getStatusLabel(materialListData.status)}
                </span>
                <div className="flex items-center text-gray-700">
                  <Scale className="mr-2" size={18} />
                  <span className="font-medium">
                    Total Weight:{" "}
                    {materialListData.totalWeight || calculateTotalWeight()} kg
                  </span>
                </div>
                <div className="text-sm text-gray-500">
                  <Clock className="inline mr-1" size={14} />
                  <span>
                    Last updated: {formatDate(materialListData?.updatedAt)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left column - Material list */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="lg:col-span-2"
          >
            {/* Materials section */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-8">
              <div
                className="flex justify-between items-center p-6 cursor-pointer"
                onClick={() => toggleSection("materials")}
              >
                <div className="flex items-center">
                  <Clipboard className="mr-3 text-blue-600" size={22} />
                  <h2 className="text-xl font-semibold text-gray-900">
                    Materials
                  </h2>
                </div>
                {expandedSections.materials ? (
                  <ChevronUp className="text-gray-500" size={20} />
                ) : (
                  <ChevronDown className="text-gray-500" size={20} />
                )}
              </div>

              <AnimatePresence>
                {expandedSections?.materials && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="px-6 pb-6">
                      {!materialListData.materialList ||
                      !Array.isArray(materialListData.materialList) ||
                      materialListData.materialList.length === 0 ? (
                        <div className="py-8 text-center">
                          <p className="text-gray-500">
                            No materials available
                          </p>
                        </div>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead>
                              <tr>
                                <th className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Product No.
                                </th>
                                <th className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Description
                                </th>
                                <th className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Category
                                </th>
                                <th className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Quantity
                                </th>
                                <th className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Weight (kg)
                                </th>
                                <th className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Total (kg)
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {materialListData.materialList.map(
                                (material, index) => (
                                  <motion.tr
                                    key={material._id || index}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{
                                      duration: 0.3,
                                      delay: index * 0.05,
                                    }}
                                    className="hover:bg-gray-50"
                                  >
                                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                      {material.product_no || "N/A"}
                                    </td>
                                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">
                                      {material.description || "N/A"}
                                    </td>
                                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">
                                      <span className="inline-flex items-center">
                                        <Tag className="mr-1" size={14} />
                                        {material.category || "Uncategorized"}
                                      </span>
                                    </td>
                                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">
                                      {material.quantity || 0}
                                    </td>
                                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">
                                      {material.kg || 0}
                                    </td>
                                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                      {(
                                        (Number(material.kg) || 0) *
                                        (Number(material.quantity) || 0)
                                      ).toFixed(2)}
                                    </td>
                                  </motion.tr>
                                )
                              )}
                              <tr className="bg-gray-50">
                                <td
                                  colSpan={5}
                                  className="px-4 py-3 text-right text-sm font-medium text-gray-700"
                                >
                                  Total Weight:
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-gray-900">
                                  {calculateTotalWeight()} kg
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Signatures section */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-8">
              <div
                className="flex justify-between items-center p-6 cursor-pointer"
                onClick={() => toggleSection("signatures")}
              >
                <div className="flex items-center">
                  <FileText className="mr-3 text-green-600" size={22} />
                  <h2 className="text-xl font-semibold text-gray-900">
                    Signatures
                  </h2>
                </div>
                {expandedSections.signatures ? (
                  <ChevronUp className="text-gray-500" size={20} />
                ) : (
                  <ChevronDown className="text-gray-500" size={20} />
                )}
              </div>

              <AnimatePresence>
                {expandedSections.signatures && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="px-6 pb-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="border border-gray-200 rounded-lg p-4">
                          <h3 className="text-sm font-medium text-gray-700 mb-2">
                            Customer Signature
                          </h3>
                          <div className="h-32 bg-gray-50 rounded flex items-center justify-center mb-2">
                            {materialListData.customerSignature ? (
                              <img
                                src={
                                  materialListData.customerSignature ||
                                  "/placeholder.svg"
                                }
                                alt="Customer Signature"
                                className="max-h-full"
                                onError={(e) => {
                                  // Handle image loading error
                                  const target = e.target;
                                  target.onerror = null;
                                  target.src = "/placeholder.svg";
                                  console.error(
                                    "Error loading customer signature image"
                                  );
                                }}
                              />
                            ) : (
                              <span className="text-sm text-gray-400">
                                No signature available
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600">
                            Signed by:{" "}
                            <span className="font-medium">
                              {materialListData.customerNameSignature ||
                                "Not signed"}
                            </span>
                          </p>
                        </div>

                        <div className="border border-gray-200 rounded-lg p-4">
                          <h3 className="text-sm font-medium text-gray-700 mb-2">
                            Delivery Signature
                          </h3>
                          <div className="h-32 bg-gray-50 rounded flex items-center justify-center mb-2">
                            {materialListData.deliverySignature ? (
                              <img
                                src={
                                  materialListData.deliverySignature ||
                                  "/placeholder.svg"
                                }
                                alt="Delivery Signature"
                                className="max-h-full"
                                onError={(e) => {
                                  // Handle image loading error
                                  const target = e.target;
                                  target.onerror = null;
                                  target.src = "/placeholder.svg";
                                  console.error(
                                    "Error loading delivery signature image"
                                  );
                                }}
                              />
                            ) : (
                              <span className="text-sm text-gray-400">
                                No signature available
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600">
                            Signed by:{" "}
                            <span className="font-medium">
                              {materialListData.deliveryNameSignature ||
                                "Not signed"}
                            </span>
                          </p>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>

          {/* Right column - Details */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="lg:col-span-1"
          >
            {/* Details section */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-8">
              <div
                className="flex justify-between items-center p-6 cursor-pointer"
                onClick={() => toggleSection("details")}
              >
                <div className="flex items-center">
                  <FileText className="mr-3 text-purple-600" size={22} />
                  <h2 className="text-xl font-semibold text-gray-900">
                    Details
                  </h2>
                </div>
                {expandedSections.details ? (
                  <ChevronUp className="text-gray-500" size={20} />
                ) : (
                  <ChevronDown className="text-gray-500" size={20} />
                )}
              </div>

              <AnimatePresence>
                {expandedSections.details && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="px-6 pb-6">
                      <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                        <div className="sm:col-span-2">
                          <dt className="text-sm font-medium text-gray-500">
                            Project ID
                          </dt>
                          <dd className="mt-1 text-sm text-gray-900">
                            {materialListData.projectId?.$oid || "N/A"}
                          </dd>
                        </div>
                        <div className="sm:col-span-1">
                          <dt className="text-sm font-medium text-gray-500">
                            Created At
                          </dt>
                          <dd className="mt-1 text-sm text-gray-900">
                            {formatDate(materialListData?.createdAt)}
                          </dd>
                        </div>
                        <div className="sm:col-span-1">
                          <dt className="text-sm font-medium text-gray-500">
                            Updated At
                          </dt>
                          <dd className="mt-1 text-sm text-gray-900">
                            {formatDate(materialListData?.updatedAt)}
                          </dd>
                        </div>
                        <div className="sm:col-span-2">
                          <dt className="text-sm font-medium text-gray-500">
                            Comments
                          </dt>
                          <dd className="mt-1 text-sm text-gray-900 bg-gray-50 p-3 rounded-md">
                            {materialListData.comments ||
                              "No comments available"}
                          </dd>
                        </div>
                        {/* <div className="sm:col-span-2">
                          <dt className="text-sm font-medium text-gray-500">
                            Transfer Permissions
                          </dt>
                          <dd className="mt-1 text-sm">
                            <ul className="space-y-2">
                              <li className="flex items-center">
                                <span
                                  className={`w-3 h-3 rounded-full mr-2 ${
                                    materialListData.transferMaterialListsPermisssion
                                      ? "bg-green-500"
                                      : "bg-red-500"
                                  }`}
                                ></span>
                                <span>Transfer Material Lists: </span>
                                <span
                                  className={`ml-1 font-medium ${
                                    materialListData.transferMaterialListsPermisssion
                                      ? "text-green-600"
                                      : "text-red-600"
                                  }`}
                                >
                                  {materialListData.transferMaterialListsPermisssion
                                    ? "Allowed"
                                    : "Not Allowed"}
                                </span>
                              </li>
                              <li className="flex items-center">
                                <span
                                  className={`w-3 h-3 rounded-full mr-2 ${
                                    materialListData.permisssionUserToAdmin
                                      ? "bg-green-500"
                                      : "bg-red-500"
                                  }`}
                                ></span>
                                <span>User to Admin: </span>
                                <span
                                  className={`ml-1 font-medium ${
                                    materialListData.permisssionUserToAdmin
                                      ? "text-green-600"
                                      : "text-red-600"
                                  }`}
                                >
                                  {materialListData.permisssionUserToAdmin
                                    ? "Allowed"
                                    : "Not Allowed"}
                                </span>
                              </li>
                              <li className="flex items-center">
                                <span
                                  className={`w-3 h-3 rounded-full mr-2 ${
                                    materialListData.permisssionAdminToUser
                                      ? "bg-green-500"
                                      : "bg-red-500"
                                  }`}
                                ></span>
                                <span>Admin to User: </span>
                                <span
                                  className={`ml-1 font-medium ${
                                    materialListData.permisssionAdminToUser
                                      ? "text-green-600"
                                      : "text-red-600"
                                  }`}
                                >
                                  {materialListData.permisssionAdminToUser
                                    ? "Allowed"
                                    : "Not Allowed"}
                                </span>
                              </li>
                            </ul>
                          </dd>
                        </div>
                        <div className="sm:col-span-2">
                          <dt className="text-sm font-medium text-gray-500">
                            Notifications
                          </dt>
                          <dd className="mt-1 text-sm">
                            <ul className="space-y-2">
                              <li className="flex items-center">
                                <span
                                  className={`w-3 h-3 rounded-full mr-2 ${
                                    materialListData.notificationToAdminCreate
                                      ? "bg-green-500"
                                      : "bg-red-500"
                                  }`}
                                ></span>
                                <span>Notify Admin on Create: </span>
                                <span
                                  className={`ml-1 font-medium ${
                                    materialListData.notificationToAdminCreate
                                      ? "text-green-600"
                                      : "text-red-600"
                                  }`}
                                >
                                  {materialListData.notificationToAdminCreate
                                    ? "Yes"
                                    : "No"}
                                </span>
                              </li>
                              <li className="flex items-center">
                                <span
                                  className={`w-3 h-3 rounded-full mr-2 ${
                                    materialListData.notificationToAdminEdit
                                      ? "bg-green-500"
                                      : "bg-red-500"
                                  }`}
                                ></span>
                                <span>Notify Admin on Edit: </span>
                                <span
                                  className={`ml-1 font-medium ${
                                    materialListData.notificationToAdminEdit
                                      ? "text-green-600"
                                      : "text-red-600"
                                  }`}
                                >
                                  {materialListData.notificationToAdminEdit
                                    ? "Yes"
                                    : "No"}
                                </span>
                              </li>
                            </ul>
                          </dd>
                        </div> */}
                      </dl>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Delivery Info */}
            {/* <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="bg-white rounded-xl shadow-sm overflow-hidden mb-8"
            >
              <div className="p-6">
                <div className="flex items-center mb-4">
                  <Truck className="mr-3 text-orange-600" size={22} />
                  <h2 className="text-xl font-semibold text-gray-900">
                    Delivery Information
                  </h2>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-500">
                      Delivery Status
                    </span>
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-medium ${
                        materialListData.status === "closed"
                          ? "bg-green-100 text-green-800"
                          : "bg-yellow-100 text-yellow-800"
                      }`}
                    >
                      {materialListData.status === "closed"
                        ? "Delivered"
                        : "Pending"}
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-500">
                      Transfer Weight
                    </span>
                    <span className="text-sm font-medium text-gray-900">
                      {materialListData.transferWeight || "0"} kg
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-500">
                      Material List Form
                    </span>
                    <span
                      className={`text-sm font-medium ${
                        materialListData.isMaterialListForm
                          ? "text-green-600"
                          : "text-red-600"
                      }`}
                    >
                      {materialListData.isMaterialListForm ? "Yes" : "No"}
                    </span>
                  </div>
                </div>
              </div>
            </motion.div> */}
          </motion.div>
        </div>
      </div>
   
    </MainLayout>
  );
}
